import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { SimpleButton } from '../../common/Buttons/SimpleButton';
import RaiseSignOff from '../../pages/chat/ChatMessage/SubHeader/RaiseSignoff';
import {
    getTicketActionsList,
    isIndividualMilestoneStatusInRange,
    MilestoneSubStatus,
} from '../../pages/Ticket/TicketController';
import { canRaisePavMilestoneSignOff } from './pavConfig';

const SIGN_OFF_ACTION_KEYS = new Set([
    'approve_ticket_milestone',
    'reject_ticket_milestone',
    'approve_ticket',
    'reject_ticket',
]);

const belongsToMilestone = (item, milestoneId) => {
    const itemMilestoneId = item?.milestone_id || item?.ticket_milestone_id;
    return itemMilestoneId === undefined || itemMilestoneId === null || String(itemMilestoneId) === String(milestoneId);
};

export default function PavMilestoneActions({ ticket, milestone, compact = false, hasSavedMilestoneAsset = false, onRaiseSignOff, onSignOffFormBack }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);
    const ticketId = ticket?.id || ticket?.ticket_id;

    const scopedTicketAgents = useMemo(() => (
        (Array.isArray(ticket?.agents) ? ticket.agents : []).filter((agent) => belongsToMilestone(agent, milestone?.id))
    ), [ticket?.agents, milestone?.id]);

    const scopedTicket = useMemo(() => ({
        ...ticket,
        id: ticketId,
        milestones: milestone ? [milestone] : [],
        agents: scopedTicketAgents,
    }), [ticket, ticketId, milestone, scopedTicketAgents]);

    const signOffActions = useMemo(() => {
        if (!milestone || !newLoggedUserData?.user || !newLoggedUserData?.org || !Array.isArray(newLoggedUserData?.orgMemberships)) return [];
        const availableActions = getTicketActionsList(scopedTicket, newLoggedUserData).filter((action) => SIGN_OFF_ACTION_KEYS.has(action.key));
        const customerActions = availableActions.filter((action) => ['approve_ticket', 'reject_ticket'].includes(action.key));
        return customerActions.length > 0
            ? customerActions
            : availableActions.filter((action) => ['approve_ticket_milestone', 'reject_ticket_milestone'].includes(action.key));
    }, [milestone, newLoggedUserData, scopedTicket]);

    const isSignOffRejected = [MilestoneSubStatus.SIGN_OFF_REJECTED, MilestoneSubStatus.CUSTOMER_SIGN_OFF_REJECTED].includes(milestone?.milestone_status);
    const isSignOffRaised = !isSignOffRejected && isIndividualMilestoneStatusInRange(milestone, MilestoneSubStatus.SIGN_OFF_REQUESTED);
    const isPendingFromCustomer = ticket?.is_customer_sign_off_required === true
        && ticket?.org_id !== newLoggedUserData?.org?.id
        && milestone?.milestone_status === MilestoneSubStatus.SIGN_OFF_REQUESTED;
    const canRaiseSignOff = canRaisePavMilestoneSignOff({
        ticket,
        milestone,
        hasSavedMilestoneAsset,
    });

    if (isPendingFromCustomer) {
        return (
            <span className={`${compact ? 'h-8 px-2.5' : 'h-10 px-4'} inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-orange-400 bg-orange-50 text-xs font-semibold text-scogoorange orange-gradient`}>
                <span className='material-icons-outlined text-sm'>hourglass_top</span>
                Pending from Customer
            </span>
        );
    }

    if (!isSignOffRejected && signOffActions.length > 0) {
        return (
            <div className='flex shrink-0 items-center gap-2'>
                {signOffActions.map((action) => {
                    const isReject = action.key.includes('reject');
                    return (
                        <SimpleButton
                            key={action.key}
                            type='button'
                            buttonIconComponent={<span className='material-icons text-sm'>{isReject ? 'close' : 'done'}</span>}
                            btnClass={`${compact ? 'h-8 px-2.5 text-xs' : 'h-10 px-4 text-sm'} flex shrink-0 items-center justify-center gap-1.5 rounded-lg border font-semibold shadow-sm transition-colors ${isReject ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100' : 'border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}
                            textOrComponent={isReject ? 'Reject signoff' : 'Approve signoff'}
                            onClick={() => action.onClick(scopedTicket, dispatch, { navigate, onBack: onSignOffFormBack })}
                        />
                    );
                })}
            </div>
        );
    }

    if (isSignOffRaised) {
        return (
            <span className={`${compact ? 'h-8 px-2.5' : 'h-10 px-4'} inline-flex shrink-0 items-center rounded-lg border border-blue-200 bg-blue-50 text-xs font-semibold text-blue-700`}>
                Signoff raised
            </span>
        );
    }

    if (!canRaiseSignOff) return null;

    const raiseButton = (
        <SimpleButton
            type='button'
            buttonIconComponent={<span className='material-icons text-sm'>assignment_turned_in</span>}
            btnClass={`${compact ? 'h-8 px-2.5 text-xs' : 'h-10 px-4 text-sm'} flex shrink-0 items-center justify-center gap-1.5 rounded-lg border border-scogoprimary bg-white font-semibold text-scogoprimary shadow-sm transition-colors hover:bg-scogolightblue`}
            textOrComponent='Raise signoff'
            onClick={onRaiseSignOff}
        />
    );

    if (typeof onRaiseSignOff === 'function') return raiseButton;

    return (
        <RaiseSignOff
            className='shrink-0'
            org_id={ticket?.org_id}
            selectedMilestone={milestone}
            ticketId={ticketId}
            formName={`pav_raise_signoff_${milestone?.id}`}
            helpdesk_id={ticket?.helpdesk_id}
        >
            {raiseButton}
        </RaiseSignOff>
    );
}
